import React from 'react'
import {
  Box,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Typography,
  List,
  ListItemButton,
  CircularProgress,
  Paper
} from '@mui/material'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import SearchIcon from '@mui/icons-material/Search'
import { useNavigate } from 'react-router-dom'
import { getCategories } from '../Apis/CategoriesApis'

const NavSecMobile = () => {
  const [searchQuery, setSearchQuery] = React.useState('')
  const [categories, setCategories] = React.useState([])
  const [filteredCategories, setFilteredCategories] = React.useState([])
  const [showDropdown, setShowDropdown] = React.useState(false)
  const [loading, setLoading] = React.useState(false)

  const debounceTimer = React.useRef(null)
  const navigate = useNavigate()

  // Fetch categories on mount
  React.useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await getCategories()
        console.log('Categories API response:', data) // Debug log
        if (data && data.status && Array.isArray(data.categories)) {
          setCategories(data.categories)
          console.log('Categories set:', data.categories.length) // Debug log
        }
      } catch (error) {
        console.error('Error fetching categories:', error)
      }
    }
    fetchCategories()
  }, [])

  // Handle search input
  const handleSearchChange = (e) => {
    const value = e.target.value
    console.log('Input changed:', value) // Debug log
    
    setSearchQuery(value)

    // Clear previous debounce timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
    }

    // If empty, clear everything immediately
    if (!value.trim()) {
      setFilteredCategories([])
      setShowDropdown(false)
      setLoading(false)
      return
    }

    // Show loading state
    setLoading(true)
    setShowDropdown(true)

    // Debounce the filtering
    debounceTimer.current = setTimeout(() => {
      const filtered = categories.filter(category =>
        category.name.toLowerCase().includes(value.toLowerCase())
      )
      console.log('Filtered results:', filtered) // Debug log
      setFilteredCategories(filtered)
      setLoading(false)
    }, 300)
  }

  const handleCategoryClick = (category) => {
    // Clear search UI immediately
    setSearchQuery('')
    setFilteredCategories([])
    setShowDropdown(false)

    // Navigate with replace if on same route, otherwise push
    const currentPath = window.location.pathname
    const targetPath = `/service/${category.id}`
    
    if (currentPath === targetPath) {
      // Same page - force reload by navigating away and back
      navigate('/', { replace: true })
      setTimeout(() => {
        navigate(targetPath, {
          state: {
            ...category,
            scrollTo: 'service-details'
          }
        })
      }, 0)
    } else {
      // Different page - normal navigation
      navigate(targetPath, {
        state: {
          ...category,
          scrollTo: 'service-details'
        }
      })
    }
  }

  const handleSearchSubmit = (e) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    
    if (filteredCategories.length > 0) {
      handleCategoryClick(filteredCategories[0])
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSearchSubmit()
    }
  }

  // Cleanup debounce timer on unmount
  React.useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
    }
  }, [])

  return (
    <Box
      sx={{
        display: { xs: 'block', md: 'none' },
        pt: 3,
        pb: 2,
        px: 1
      }}
    >
      {/* Location */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.2 }}>
        <LocationOnIcon sx={{ color: '#ff6a00', mt: '2px' }} />

        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 700 }}>
            Madhapur
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography sx={{ fontSize: 16, color: '#8C8C8C' }}>
              Select your location
            </Typography>
            <KeyboardArrowDownIcon sx={{ color: '#8C8C8C' }} />
          </Box>
        </Box>
      </Box>

      {/* Search */}
      <Box sx={{ mt: 2, position: 'relative' }}>
        <OutlinedInput
          fullWidth
          value={searchQuery}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            console.log('Input focused')
            if (searchQuery) setShowDropdown(true)
          }}
          placeholder="Which service are you looking for?"
          autoComplete="off"
          inputProps={{
            'aria-label': 'search services'
          }}
          endAdornment={
            <InputAdornment position="end">
              <IconButton 
                onClick={handleSearchSubmit}
                edge="end"
                type="button"
                aria-label="search"
              >
                {loading ? (
                  <CircularProgress size={22} />
                ) : (
                  <SearchIcon sx={{ color: '#ff6a00', fontSize: 28 }} />
                )}
              </IconButton>
            </InputAdornment>
          }
          sx={{
            height: 54,
            borderRadius: '10px',
            backgroundColor: '#fff',
            color: '#000',
            '& input': {
              color: '#000'
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#D9D9D9'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#ff6a00'
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#ff6a00'
            }
          }}
        />

        {/* Dropdown */}
        {showDropdown && !loading && filteredCategories.length > 0 && (
          <Paper
            elevation={3}
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              mt: 0.5,
              zIndex: 10,
              borderRadius: '8px',
              maxHeight: 300,
              overflowY: 'auto'
            }}
          >
            <List disablePadding>
              {filteredCategories.map(category => (
                <ListItemButton
                  key={category.id}
                  onClick={() => handleCategoryClick(category)}
                  sx={{
                    py: 1.5,
                    '&:hover': { backgroundColor: '#FFF3E6' }
                  }}
                >
                  <Box>
                    <Typography fontSize={14} fontWeight={500}>
                      {category.name}
                    </Typography>

                    {category.description && (
                      <Typography fontSize={12} color="#8C8C8C">
                        {category.description}
                      </Typography>
                    )}
                  </Box>
                </ListItemButton>
              ))}
            </List>
          </Paper>
        )}

        {/* No Results */}
        {showDropdown && searchQuery && !loading && filteredCategories.length === 0 && (
          <Paper
            elevation={3}
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              mt: 0.5,
              zIndex: 10,
              borderRadius: '8px'
            }}
          >
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography fontSize={14} color="#8C8C8C">
                No services found
              </Typography>
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  )
}

export default NavSecMobile